import { Ban, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import type { ChatBan } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { isBanExpired } from '../api/mappers';
import { useBanMutations } from '../hooks/use-ban-mutations';
import { useBans } from '../hooks/use-bans';

import { BanDialog } from './ban-dialog';
import { ConfirmDialog } from './confirm-dialog';

const targetOf = (ban: ChatBan) => ban.ip ?? ban.visitorId ?? '';

/**
 * Banned visitors and addresses. A banned shopper keeps a working widget; what they send never
 * reaches the inbox. Reading and changing bans both need `conversations.manage`.
 */
export function BansTab({ storeId }: { storeId: string }) {
  const { t } = useTranslation('chatSettings');
  const format = useFormat();
  const query = useBans(storeId);
  const { remove } = useBanMutations(storeId);
  const [adding, setAdding] = useState(false);
  const [lifting, setLifting] = useState<ChatBan | null>(null);

  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!query.data) return <Skeleton className="h-40" aria-busy="true" />;
  const bans = query.data.pages.flatMap((page) => page.items);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground max-w-2xl text-sm">{t('bans.description')}</p>
        <Button type="button" onClick={() => setAdding(true)}>
          <Plus aria-hidden />
          {t('bans.add')}
        </Button>
      </div>
      {bans.length === 0 ? (
        <EmptyState
          icon={Ban}
          title={t('bans.emptyTitle')}
          description={t('bans.emptyDescription')}
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('bans.columns.target')}</TableHead>
                <TableHead>{t('bans.columns.reason')}</TableHead>
                <TableHead>{t('bans.columns.expires')}</TableHead>
                <TableHead>{t('bans.columns.created')}</TableHead>
                <TableHead>
                  <span className="sr-only">{t('bans.columns.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bans.map((ban) => (
                <TableRow key={ban.id} data-row-id={ban.id}>
                  <TableCell>
                    <span className="text-muted-foreground block text-xs">
                      {t(`bans.targets.${ban.ip ? 'ip' : 'visitor'}`)}
                    </span>
                    <span className="font-mono text-xs break-all">{targetOf(ban)}</span>
                  </TableCell>
                  <TableCell className="max-w-64 text-sm whitespace-normal">
                    {ban.reason ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {ban.expiresAt === null ? (
                      t('bans.permanent')
                    ) : (
                      <span className="flex flex-wrap items-center gap-2">
                        <span title={format.utc(ban.expiresAt)}>
                          {format.dateTime(ban.expiresAt)}
                        </span>
                        {isBanExpired(ban) ? (
                          <Badge variant="outline">{t('bans.expired')}</Badge>
                        ) : null}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    <span title={format.utc(ban.createdAt)}>{format.date(ban.createdAt)}</span>
                    {ban.createdBy ? (
                      <span className="text-muted-foreground block text-xs">
                        {t('bans.by', { name: ban.createdBy.name })}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={t('bans.unbanLabel', { target: targetOf(ban) })}
                      onClick={() => setLifting(ban)}
                    >
                      {t('bans.unban')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {query.hasNextPage ? (
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {t('bans.loadMore')}
          </Button>
        </div>
      ) : null}
      {adding ? <BanDialog storeId={storeId} onOpenChange={setAdding} /> : null}
      <ConfirmDialog
        open={lifting !== null}
        onOpenChange={(open) => {
          if (!open) setLifting(null);
        }}
        title={t('bans.unbanTitle', { target: lifting ? targetOf(lifting) : '' })}
        description={t('bans.unbanDescription')}
        confirmLabel={t('bans.unban')}
        pending={remove.isPending}
        onConfirm={() => {
          if (lifting) remove.mutate(lifting.id, { onSettled: () => setLifting(null) });
        }}
      />
    </div>
  );
}
